import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faSpinnerThird } from "@fortawesome/sharp-thin-svg-icons"
import { SxProps } from "@mui/material"
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import React from "react"
import { buildAutocompleteSx, makeRenderOption, Option, renderChips, SELECT_ALL_ID } from "./shared"

type Props = {
    options?: Option[]
    value?: Option | Option[] | null
    onChange?: (value: Option | Option[] | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
    required?: boolean
    fullWidth?: boolean
    size?: "small" | "medium" | "large"
    disablePortal?: boolean
    containerHeight?: string
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: Option,
        { selected }: { selected: boolean }
    ) => React.ReactNode
    multiple?: boolean
    sx?: SxProps
    disableClearable?: boolean
    loading?: boolean
    showSelectAll?: boolean
    placement?: "top" | "bottom" | "top-start" | "top-end" | "bottom-start" | "bottom-end"
}

const MuiAutoComplete: React.FC<Props> = ({
    options = [],
    value,
    onChange,
    label,
    placeholder,
    disablePortal = true,
    disabled = false,
    error = false,
    helperText,
    required = false,
    fullWidth = false,
    containerHeight,
    size = "medium",
    multiple = false,
    sx = {},
    renderOption,
    disableClearable = false,
    loading = false,
    showSelectAll = false,
    placement,
}) => {
    const SELECT_ALL_OPTION: Option = {
        id: SELECT_ALL_ID,
        title: "Select All",
    }
    const computedOptions = multiple && showSelectAll && options.length ? [SELECT_ALL_OPTION, ...options] : options
    const filterOptions = createFilterOptions<Option>({
        stringify: option => `${option.title} ${option.search_key ?? ""}`,
    })
    const isAllSelected = Array.isArray(value) && (value as Option[]).length === options.length
    const isIndeterminate =
        Array.isArray(value) && (value as Option[]).length > 0 && (value as Option[]).length < options.length
    return (
        <Autocomplete
            multiple={multiple}
            disablePortal={disablePortal}
            disableCloseOnSelect={multiple}
            options={computedOptions}
            value={value}
            getOptionDisabled={(option: any) => !!option.disabled}
            slotProps={{
                popper: placement ? { placement } : undefined,
                listbox: { sx: { maxHeight: "260px" } },
            }}
            popupIcon={
                loading ? <FontAwesomeIcon icon={faSpinnerThird} spinPulse /> : <FontAwesomeIcon icon={faAngleDown} />
            }
            disableClearable={disableClearable}
            onChange={(_, newValue) => {
                if (!multiple) {
                    onChange?.(newValue as Option)
                    return
                }

                const values = newValue as Option[]

                if (showSelectAll) {
                    const selectAllClicked = values.find(v => v.id === SELECT_ALL_ID)
                    if (selectAllClicked) {
                        if (isAllSelected) {
                            onChange?.([])
                        } else {
                            onChange?.(options)
                        }
                        return
                    }
                }

                onChange?.(values)
            }}
            getOptionLabel={(option: any) => option.title || ""}
            renderOption={
                (renderOption ?? makeRenderOption({ multiple, showSelectAll, isAllSelected, isIndeterminate })) as any
            }
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            disabled={disabled}
            fullWidth={fullWidth}
            filterOptions={filterOptions as any}
            onClick={() => {}}
            limitTags={multiple ? 1 : undefined}
            size={size === "large" ? "medium" : size}
            renderValue={multiple ? renderChips : undefined}
            sx={buildAutocompleteSx({ error, disabled, containerHeight, size, sx, multiple })}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    required={required}
                    variant="standard"
                />
            )}
        />
    )
}

export default MuiAutoComplete
